// Reading a class-note back into the "Đánh giá tiết học" form. Split out of the editor so the
// round-trip - the part that silently duplicated text - is testable without JSX.

import type { QltClassNoteData } from '../../api/types';
import { composeSentence, orderItems, restoreChecklist, toggleItem, type ChecklistText, type SentenceRule } from './checklistText.ts';

// data.classMark is a plain 0-10 number (lms-fe edits it as one)
export const CLASS_MARK_SCORES = [10, 9, 8, 7, 6, 5];
export const CLASS_MARK_LOW_SCORES = [4, 3, 2, 1, 0];

/** The picked score when it sits on the page not shown, for the pager's badge; null otherwise. */
export function markOnOtherPage(mark: number | null, lowPage: boolean): number | null {
  const other = lowPage ? CLASS_MARK_SCORES : CLASS_MARK_LOW_SCORES;
  return mark !== null && other.indexOf(mark) >= 0 ? mark : null;
}

// the retired band chips, by id and by the badge seeded records stored; read only when classMark is absent
const LEGACY_BAND_MARKS: Array<[string, string, number]> = [
  ['excellent', '10-9', 10],
  ['good', '9-8', 9],
  ['fair', '8-7', 8],
  ['weak', '7-6', 7],
  ['poor', '6-5', 6],
  ['bad', 'Dưới 5', 4],
];

// MOCK M5: verbatim from the "Nhận xét lớp" export - the improvement list, for 8-7 and below.
export const CLASS_COMMENT_IMPROVE = [
  'Mất tập trung',
  'Chưa hoàn thành nhiệm vụ',
  'Không tham gia',
  'Nề nếp kém',
  'Thiếu động lực',
  'Khó khăn trong việc quản lý thời gian',
];

// MOCK M5: the export and lms-fe have no positive list; 'Hăng hái phát biểu' is lms-fe's, the rest mirror the list above.
export const CLASS_COMMENT_POSITIVE = [
  'Tập trung nghe giảng',
  'Hoàn thành tốt nhiệm vụ',
  'Hăng hái phát biểu',
  'Tích cực tham gia',
  'Nề nếp tốt',
];

/** Every preset, for reading back a flat classComment whatever mark it was written under. */
export const CLASS_COMMENT_PRESETS = CLASS_COMMENT_POSITIVE.concat(CLASS_COMMENT_IMPROVE);

// presets carry no subject, so the generated sentence opens with "Lớp"
export const CLASS_COMMENT_RULE: SentenceRule = { subject: 'Lớp', order: CLASS_COMMENT_PRESETS };

/** The comment chips a mark offers: positive for 10-9, improvement for 8 and below; none until a mark is picked. */
export function commentPresetsFor(mark: number | null): string[] {
  if (mark === null) return [];
  return mark >= 9 ? CLASS_COMMENT_POSITIVE : CLASS_COMMENT_IMPROVE;
}

/** Ticked comments that still fit a newly picked mark; the rest are unticked. */
export function tagsForMark(tags: string[], mark: number | null): string[] {
  const allowed = commentPresetsFor(mark);
  return tags.filter((tag) => allowed.indexOf(tag) >= 0);
}

/** Unticks what the mark's list hides and takes their generated sentence out of the box; typed text stays. */
export function commentForMark(state: ChecklistText, mark: number | null): ChecklistText {
  const kept = tagsForMark(state.ticks, mark);
  return state.ticks.filter((tag) => kept.indexOf(tag) < 0).reduce((current, tag) => toggleItem(current, CLASS_COMMENT_RULE, tag), state);
}

/** Reopening never rewrites the stored text: ticks the mark's list hides drop, their words stay as typed text. */
export function restoreForMark(state: ChecklistText, mark: number | null): ChecklistText {
  const kept = tagsForMark(state.ticks, mark);
  return kept.length === state.ticks.length ? state : restoreChecklist(CLASS_COMMENT_RULE, kept, state.text);
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}

/** The stored mark; a band-only record from the retired chips reads as that band's old mark. */
export function markOf(data?: QltClassNoteData): number | null {
  if (!data) return null;
  const mark = data.classMark;
  if (typeof mark === 'number' && isFinite(mark)) return mark;
  const stored = asText(data.classMarkBand);
  for (let i = 0; i < LEGACY_BAND_MARKS.length; i += 1) {
    const [id, badge, value] = LEGACY_BAND_MARKS[i];
    if (stored === id || stored === badge) return value;
  }
  return null;
}

export function classMarkLabel(data?: QltClassNoteData): string {
  const mark = markOf(data);
  return mark === null ? '' : String(mark);
}

const SEPARATOR = '; ';

function flatComment(tags: readonly string[], note: string): string {
  return tags.concat(note ? [note] : []).join(SEPARATOR);
}

/** Splits a record into ticked presets and leftover free text. Without this the already-joined
    classComment lands in the note box whole and every save re-appends the presets inside it. */
export function hydrateComment(data?: QltClassNoteData): { tags: string[]; note: string } {
  if (!data) return { tags: [], note: '' };
  const comment = asText(data.classComment).trim();
  // structured fields count only while they still spell classComment: QLT web rewrites classComment alone
  if (Array.isArray(data.classCommentTags)) {
    const structured = { tags: asList(data.classCommentTags), note: asText(data.classCommentNote).trim() };
    if (flatComment(structured.tags, structured.note) === comment) return structured;
  }
  const tags: string[] = [];
  const rest: string[] = [];
  comment.split(';').forEach((part) => {
    const value = part.trim();
    if (!value) return;
    if (CLASS_COMMENT_PRESETS.indexOf(value) < 0) rest.push(value);
    else if (tags.indexOf(value) < 0) tags.push(value);
  });
  return { tags, note: rest.join(SEPARATOR) };
}

/** The comment box and its ticks. A record this editor saved keeps its text as is; an older "a; b; note"
    record, or one QLT web rewrote, becomes the generated sentence followed by its free text. */
export function hydrateCommentText(data?: QltClassNoteData): ChecklistText {
  const comment = asText(data?.classComment).trim();
  if (data && Array.isArray(data.classCommentTags) && asText(data.classCommentText) === comment) {
    return restoreChecklist(CLASS_COMMENT_RULE, asList(data.classCommentTags), comment);
  }
  const legacy = hydrateComment(data);
  if (legacy.tags.length === 0) return { ticks: [], text: comment, owned: [] };
  const sentence = composeSentence(CLASS_COMMENT_RULE, legacy.tags);
  return restoreChecklist(CLASS_COMMENT_RULE, legacy.tags, legacy.note ? `${sentence} ${legacy.note}` : sentence);
}

/** What the editor writes back: classComment is the box text; classCommentText marks it as ours so a QLT web edit is spotted. */
export function commentPayload(ticks: string[], text: string): Pick<QltClassNoteData, 'classComment' | 'classCommentText' | 'classCommentTags' | 'classCommentNote'> {
  const trimmed = text.trim();
  // classCommentNote is the old split-out free text; undefined drops it from the saved data
  return { classComment: trimmed, classCommentText: trimmed, classCommentTags: orderItems(CLASS_COMMENT_PRESETS, ticks), classCommentNote: undefined };
}
